import moment from 'moment';
import { environment } from '@app/environment';
import { DemoozInputAddress } from '@demooz/components';
import { Operation, Challenges, ChallengesLevel, Challenge, ChallengeCategorie, ChallengeType, ResponseChallenge, Question } from '@app/models';

export class AmbassadorAddressGPS {
    type: string = 'Point';
    coordinates: Array<number> = [];
    constructor(latitude: number, longitude: number){
        this.coordinates = [longitude, latitude];
    }
    
    static FromDemoozInputAddress(input: DemoozInputAddress): AmbassadorAddressGPS {
        return new AmbassadorAddressGPS(
            input.latitude,
            input.longitude            
        );
    }
}

export class AmbassadorAddress {
    streetNumber: string;
    route: string;
    city: string;
    postalCode: string;
    country: string;   
    
    constructor(streetNumber: string, route: string, postalCode: string, city: string, country: string) {
        this.streetNumber = streetNumber;
        this.route = route;
        this.postalCode = postalCode;
        this.city = city;
        this.country = country;
    } 
    
    toString(): string {
        // TO DO - Prendre en compte le format de l'adresse en fonction du pays ?
        return `${this.streetNumber ? this.streetNumber + ' ' : ''}${this.route ? this.route + ', ' : ''}${this.postalCode ? this.postalCode + ' ' : ''}${this.city || ''}${this.country ? ', ' + this.country : ''}`;
    }
    
    static FromLead(lead: any): AmbassadorAddress {
        return new AmbassadorAddress(
            lead.address.streetNumber,
            lead.address.route,
            lead.address.postalCode,
            lead.address.city,
            lead.address.country
        );
    }
    
    static FromAmbassador(ambassador: any): AmbassadorAddress {
        return new AmbassadorAddress(
            ambassador.address.streetNumber,
            ambassador.address.route,
            ambassador.address.postalCode,
            ambassador.address.city,
            ambassador.address.country
        );
    }
    
    static FromDemoozInputAddress(input: DemoozInputAddress): AmbassadorAddress {
        return new AmbassadorAddress(
            input.streetNumber,
            input.route,
            input.postalCode,
            input.city,
            input.country
        );
    }
}

export class AmbassadorOrderProduct {
    ID: string;
    title: string;             
    media: string;
}

export class AmbassadorOrder {
    ID: string;  
    shopProduct: AmbassadorOrderProduct;
    quantity: number;
    costInPointsTotal: number;
    status: string;
    message: string;
    _: {
        created: Date
    }
}

export class AmbassadorUpdate {
    pictureURL: string;   
    gender: string;
    firstName: string;
    lastName: string;
    email: string;
    phone: string;
    productIDs: Array<string>;
    address: AmbassadorAddress;
    gps: AmbassadorAddressGPS;
    biography?: string;
    isVisible?: boolean;
    acceptNotifications: boolean;
    languageID?: string;
    password?: string;
}

export class Ambassador {
    
    value: any = null;
    challenges: Challenges|null = null;
    
    constructor(value: any = null){
        this.value = value;        
    }
    
    get pcd(): any|undefined {
        return this.value?._pcd;
    }
    
    get score(): any|undefined {
        return this.value?._pcd?.score;
    }
    
    get demoozInputAddressValues(): DemoozInputAddress {
        let address = AmbassadorAddress.FromAmbassador(this.value);        
        return new DemoozInputAddress(
            this.value?.address?.streetNumber,
            this.value?.address?.route,
            this.value?.address?.city,
            this.value?.address?.country,
            this.value?.address?.postalCode,
            this.value?.gps?.coordinates[1],
            this.value?.gps?.coordinates[0],
            address.toString()
        );
    }
    
    get responses(): Array<any> {
        return this.value?.responses || [];
    }
    
    get matches(): Array<any> {        
        return this.value?.matches || [];
    }
    
    get matchesActives(): Array<any> {        
        return (this.matches.filter( (m: any) => m.status !== "archived") || [])
            .sort((a: any, b: any) => {
                let matchValidatedA = (a.validation?.owner?.hasValidated === true && a.validation?.tester?.hasValidated === true);
                let matchValidatedB = (b.validation?.owner?.hasValidated === true && b.validation?.tester?.hasValidated === true);
                if(!matchValidatedA && matchValidatedB){
                    return -1;
                }
                else if(matchValidatedA && !matchValidatedB){
                    return 1;
                }
                else{
                    return (new Date(parseInt(b._.created)).getTime() - new Date(parseInt(a._.created)).getTime()) 
                }
            });
    }  
    
    get matchesValidated(): Array<any> {
        return (this.matches.filter( (m: any) => 
                m.status !== "archived" 
                && m.validation?.owner?.hasValidated === true 
                && m.validation?.tester?.hasValidated === true
            ) || [])
            .sort((a: any, b: any) => (new Date(parseInt(b._.created)).getTime() - new Date(parseInt(a._.created)).getTime()) );;
    }
    
    get matchesArchived(): Array<any> {        
        return (this.matches.filter( (m: any) => m.status === "archived") || [])
            .sort((a: any, b: any) => (new Date(parseInt(b._.created)).getTime() - new Date(parseInt(a._.created)).getTime()) );
    }  
    
    get questionsNotAnswered(): Array<Question> {       
        return (this.value?.questions?.filter((q: any) => q.status === 'pending') || [])
            .sort((a: any, b: any) => (new Date(b.date).getTime() - new Date(a.date).getTime()));
    }
    
    get questionsAnswered(): Array<Question> {       
        return (this.value?.questions?.filter((q: any) => q.status === 'answered') || [])
            .sort((a: any, b: any) => (new Date(b.date).getTime() - new Date(a.date).getTime()));
    }
    
    getShopOrders(operation: Operation): Array<AmbassadorOrder> {
        let orders: Array<AmbassadorOrder> = [];
        if(this.value.shopOrders?.length > 0){
            this.value.shopOrders.forEach((o: any) => {
                o.shopProduct.media = operation.value.pwa?.media?.find((e: any) => e.ID === o.shopProduct.media)?.URL;
                o._.created = (o._.created instanceof Date ? o._.created : new Date(parseInt(o._.created)));
                orders.push(o);
            });
        }
        
        orders.sort((a, b) => {
            let order=0;
            let aOrderStatus = (a.status === 'awaiting_validation' ? 1 : 2);
            let bOrderStatus = (b.status === 'awaiting_validation' ? 1 : 2);
            if(aOrderStatus === bOrderStatus){
              let aDate = moment(a._.created);
              let bDate = moment(b._.created);
              if(aDate > bDate){
                return -1;
              }
              else if(aDate < bDate){
                return 1;
              }
            }
            else{
              order = (aOrderStatus > bOrderStatus ? 1 : -1);
            }
            return order;
        });
        
        return orders;
    }
    
    initChallenges(operation: Operation) {
        let challenges = new Challenges();
        
        let history: Array<Challenge> = [];
        
        let missions = this.filterChallengesCollection(operation, operation.value?.challenges?.missions || [], 'missions', 'mission');
        challenges.missions = missions.challenges;
        history = history.concat(missions.challengesHistory);        
        
        let flash = this.filterChallengesCollection(operation, operation.value?.challenges?.flash || [], 'flash', 'flash');
        challenges.flash = flash.challenges;
        history = history.concat(flash.challengesHistory);   
        
        (operation.value?.challenges?.levels || []).forEach((level: any, index: number) => {       
            if(index <= this.value?._pcd?.level){
                let l = this.filterChallengesCollection(operation, level, index, 'level');
                let challengesLevel: ChallengesLevel = {
                    name: level.name, 
                    earningsDisplay: level.earningsDisplay,
                    challenges: l.challenges
                };
                challenges.levels[index] = challengesLevel;
                history = history.concat(l.challengesHistory);
            }
        });       
        
        history.sort((a: any, b: any) => (new Date(parseInt(b.response.date)).getTime() - new Date(parseInt(a.response.date)).getTime()));
        challenges.history = history;
        
        this.challenges = challenges;
    }
    
    filterChallengesCollection(operation: Operation, collection: any, level: any, categoryChallenge: ChallengeCategorie): {challenges: Array<Challenge>, challengesHistory: Array<Challenge>} {
        
        let challenges: Array<Challenge> = [];
        let challengesHistory: Array<Challenge> = [];
        
        let publications = this.filterChallenges(operation, collection?.publications, categoryChallenge, 'publication', level, 1);        
        challenges = challenges.concat(publications.challengesFiltered);
        challengesHistory = challengesHistory.concat(publications.challengesHistory);
        
        let reviews = this.filterChallenges(operation, collection?.reviews, categoryChallenge, 'review', level, 1);
        challenges = challenges.concat(reviews.challengesFiltered);
        challengesHistory = challengesHistory.concat(reviews.challengesHistory);
        
        let events = this.filterChallenges(operation, collection?.events, categoryChallenge, 'event', level, 1);
        challenges = challenges.concat(events.challengesFiltered);
        challengesHistory = challengesHistory.concat(reviews.challengesHistory);
        
        let externals = this.filterChallenges(operation, collection?.externals, categoryChallenge, 'external', level, 1);
        challenges = challenges.concat(externals.challengesFiltered);
        challengesHistory = challengesHistory.concat(externals.challengesHistory);
        
        let demonstrations = this.filterChallenges(operation, [{...collection?.demonstrations, ID: environment.CHALLENGE_ID.DEMONSTRATION}], categoryChallenge, 'demonstration', level, collection.targets?.demonstrations);
        challenges = challenges.concat(demonstrations.challengesFiltered);
        challengesHistory = challengesHistory.concat(demonstrations.challengesHistory);
        
        let sales = this.filterChallenges(operation, [{...collection?.sales, ID: environment.CHALLENGE_ID.SALE}], categoryChallenge, 'sale', level, collection.targets?.sales);
        challenges = challenges.concat(sales.challengesFiltered);
        challengesHistory = challengesHistory.concat(sales.challengesHistory);
        
        return {challenges: challenges, challengesHistory: challengesHistory};
    }
    
    filterChallenges(operation: Operation, challenges: Array<Challenge>, categoryChallenge: ChallengeCategorie, typeChallenge: ChallengeType, level: any, target: number): {challengesFiltered: Array<Challenge>, challengesHistory: Array<Challenge>} {        
        
        let now = moment().format('YYYY-MM-DD');
        let levelDone = (['missions', 'flash'].indexOf(level) === -1 && level < this.value?._pcd?.level);
        let challengesFiltered: Array<Challenge> = [];
        let challengesHistory: Array<Challenge> = [];
        let statusChallengeTreated: Array<string> = [environment.CHALLENGE_VALIDATION_TYPE.VALIDATED, environment.CHALLENGE_VALIDATION_TYPE.DENIED_DEFINITELY];
        if([environment.CHALLENGE_TYPE.DEMONSTRATION, environment.CHALLENGE_TYPE.SALE].indexOf(typeChallenge) !== -1){
            statusChallengeTreated.push(environment.CHALLENGE_VALIDATION_TYPE.DENIED);
        }
        
        challenges?.forEach(c => {
            
            c.category = categoryChallenge
            c.type = typeChallenge;
            c.level = level;
                                    
            switch(c.type) {
                case 'demonstration':
                    c.title = 'CHALLENGE.DEMONSTRATION_TITLE';
                    c.description = 'CHALLENGE.DEMONSTRATION_DESCRIPTION';
                    break;
                case 'sale':
                    c.title = 'CHALLENGE.SALE_TITLE';
                    c.description = 'CHALLENGE.SALE_DESCRIPTION';                    
                    c.responseType = operation.salesContent?.responseType;
                    c.responseTextRegex = operation.salesContent?.responseTextRegex;
                    c.responseTextError = operation.salesContent?.responseTextError;                    
                    break;
                case 'external':
                    let target = '';
                    let externalsDatas = JSON.parse((this.value?.externalsDatas ? this.value?.externalsDatas : '{}'));
                    if(externalsDatas && Object.keys(externalsDatas).length > 0 && c.externalTarget?.externalDataTargetID && externalsDatas[c.externalTarget?.externalDataTargetID]){
                        target = externalsDatas[c.externalTarget?.externalDataTargetID];
                    }
                    c.description = c.description.replace(/\{\{target\}\}/gi, target);
                    break;
            }
            
            let responseDeniedDefinitely: boolean = false;
            let nbResponsesSended = 0;
            let nbResponsesValidated = 0;
            let responsesNotValidated: Array<any> = [];
            let responses = this.findResponseChallenges(c.ID, level);
            responses.forEach((r) => {      
            
                r.date = new Date(parseInt(r.date.toString()));
                      
                if([environment.CHALLENGE_VALIDATION_TYPE.AWAITING_VALIDATION, environment.CHALLENGE_VALIDATION_TYPE.VALIDATED].indexOf(r.status) !== -1){
                    nbResponsesSended++;
                    if(r.status === environment.CHALLENGE_VALIDATION_TYPE.VALIDATED){
                        nbResponsesValidated++;
                    }                        
                }     
                      
                if(statusChallengeTreated.indexOf(r.status) !== -1){                    
                    let cHistory = {...c, response: r};
                    cHistory.category = 'history';
                    challengesHistory.push(cHistory);
                    
                    if(r.status === environment.CHALLENGE_VALIDATION_TYPE.DENIED_DEFINITELY){
                        responseDeniedDefinitely = true;
                    }
                    
                }
                else{
                    responsesNotValidated.push(r);
                }
            });
            
            if(target === 0 || nbResponsesSended < target){
                if(responsesNotValidated.length === 0){
                    if(!responseDeniedDefinitely || [environment.CHALLENGE_TYPE.DEMONSTRATION, environment.CHALLENGE_TYPE.SALE].indexOf(c.type) !== -1){
                        responsesNotValidated = [null];
                    }
                }
                else if([environment.CHALLENGE_TYPE.DEMONSTRATION, environment.CHALLENGE_TYPE.SALE].indexOf(c.type) !== -1){
                    responsesNotValidated.push(null);
                }
            }
            
            responsesNotValidated.forEach((r: any) => {         
                if(c.isEnabled
                    && ((c.startDate && c.startDate <= now) && (!c.endDate || now <= c.endDate))
                    && (!c.ambassadorsStatus?.length || c.ambassadorsStatus.indexOf(this.value?.status) !== -1) 
                    && (!c.products?.length || c.products.filter((p: any) => this.value?.productIDs?.indexOf(p) !== -1).length > 0) 
                    && (!c.levels?.length || c.levels.indexOf(this.value?._pcd?.level) !== -1)){
                    let tempC: any = {...c, response: r};
                    if(r === null){
                        tempC = {...tempC, target: (levelDone ? nbResponsesValidated : target), done: nbResponsesSended};
                    }
                    challengesFiltered.push(tempC);
                }
            });
            
        });
        return {challengesFiltered: challengesFiltered, challengesHistory: challengesHistory};
    }
    
    findResponseChallenges(challengeID: string, level: any): Array<ResponseChallenge>{
        return (this.value?.responses?.filter((r: any) => r.challengeID === challengeID && r.challengeLevel == level) || []);
    }
    
    getNbMissionsDone(): number{
        let nbMissionsDone = 0;
        if(this.score){
            nbMissionsDone = (this.score.publications || 0)
                + (this.score.reviews || 0)
                + (this.score.events || 0)
                + (this.score.externals || 0)
                + (this.score.demonstrations || 0)
                + (this.score.sales || 0);
        }
        return nbMissionsDone;
    }
}
